import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
    Plus, Package, CheckCircle, Clock, Ticket, Pencil, Image, Upload, Eye, X
} from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const initialForm = {
    corte_id: '',
    lote_id: '',
    fecha: new Date().toISOString().split('T')[0],
    cantidad: 0,
    avios: [],
    maquilador_id: '',
    maquilador_nombre: '',
    responsable: '',
    observaciones: ''
};

export const Habilitacion = () => {
    const [registros, setRegistros] = useState([]);
    const [cortes, setCortes] = useState([]);
    const [empleados, setEmpleados] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modalOpen, setModalOpen] = useState(false);
    const [editMode, setEditMode] = useState(null);
    const [form, setForm] = useState(initialForm);
    const [aviosCarton, setAviosCarton] = useState(0);
    const [aviosBoton, setAviosBoton] = useState(0);
    const [aviosRemache, setAviosRemache] = useState(0);
    const [ticketFile, setTicketFile] = useState(null);
    const [ticketPreview, setTicketPreview] = useState('');
    const [evidenciaModal, setEvidenciaModal] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileInputRef = useRef(null);
    const ticketInputRef = useRef(null);

    useEffect(() => { fetchData(); }, []);

    const fetchData = async () => {
        try {
            const [habRes, cortesRes, empRes] = await Promise.all([
                axios.get(`${API_URL}/api/habilitacion`),
                axios.get(`${API_URL}/api/cortes`),
                axios.get(`${API_URL}/api/empleados`)
            ]);
            setRegistros(habRes.data);
            setCortes(cortesRes.data);
            setEmpleados(empRes.data);
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Error al cargar datos');
        } finally { setLoading(false); }
    };

    const maquiladores = empleados.filter(e => e.tipo_trabajador === 'maquilador');

    const handleCorteChange = (corteId) => {
        const corte = cortes.find(c => c.id === corteId);
        setForm(prev => ({ ...prev, corte_id: corteId, lote_id: corte ? corte.lote_id : '' }));
    };

    const handleMaquiladorChange = (maqId) => {
        const maq = empleados.find(e => e.id === maqId);
        setForm(prev => ({
            ...prev,
            maquilador_id: maqId,
            maquilador_nombre: maq ? maq.nombre : '',
            responsable: maq ? maq.nombre : ''
        }));
    };

    const buildAvios = () => {
        const avios = [];
        if (aviosCarton > 0) avios.push({ tipo: 'carton', cantidad: aviosCarton });
        if (aviosBoton > 0) avios.push({ tipo: 'boton', cantidad: aviosBoton });
        if (aviosRemache > 0) avios.push({ tipo: 'remache', cantidad: aviosRemache });
        return avios;
    };

    const resetFormState = () => {
        setForm(initialForm);
        setAviosCarton(0); setAviosBoton(0); setAviosRemache(0);
        setTicketFile(null); setTicketPreview('');
        setEditMode(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!form.corte_id || !form.cantidad) {
            toast.error('Por favor complete los campos requeridos');
            return;
        }
        const payload = { ...form, avios: buildAvios() };
        try {
            const res = await axios.post(`${API_URL}/api/habilitacion`, payload);
            if (ticketFile && res.data?.id) {
                await uploadTicketImage(res.data.id, ticketFile);
            }
            toast.success('Habilitacion registrada exitosamente');
            setModalOpen(false);
            resetFormState();
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al registrar');
        }
    };

    const handleEditSubmit = async () => {
        if (!editMode) return;
        const payload = { ...form, avios: buildAvios() };
        try {
            await axios.put(`${API_URL}/api/habilitacion/${editMode.id}`, payload);
            if (ticketFile) {
                await uploadTicketImage(editMode.id, ticketFile);
            }
            toast.success('Habilitacion actualizada');
            setModalOpen(false);
            resetFormState();
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al actualizar');
        }
    };

    const uploadTicketImage = async (habId, file) => {
        const formData = new FormData();
        formData.append('file', file);
        const token = localStorage.getItem('token');
        await axios.post(`${API_URL}/api/habilitacion/${habId}/evidencia`, formData, {
            headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
        });
    };

    const handleTicketFileChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setTicketFile(file);
            setTicketPreview(URL.createObjectURL(file));
        }
    };

    const openEditModal = (registro) => {
        setEditMode(registro);
        setForm({
            corte_id: registro.corte_id || '',
            lote_id: registro.lote_id || '',
            fecha: registro.fecha || '',
            cantidad: registro.cantidad || 0,
            avios: registro.avios || [],
            maquilador_id: registro.maquilador_id || '',
            maquilador_nombre: registro.maquilador_nombre || '',
            responsable: registro.responsable || '',
            observaciones: registro.observaciones || ''
        });
        const avios = registro.avios || [];
        setAviosCarton(avios.find(a => a.tipo === 'carton')?.cantidad || 0);
        setAviosBoton(avios.find(a => a.tipo === 'boton')?.cantidad || 0);
        setAviosRemache(avios.find(a => a.tipo === 'remache')?.cantidad || 0);
        setTicketFile(null);
        setTicketPreview(registro.evidencia_url ? `${API_URL}${registro.evidencia_url}` : '');
        setModalOpen(true);
    };

    const handleConfirmar = async (id) => {
        try {
            await axios.put(`${API_URL}/api/habilitacion/${id}/confirmar`);
            toast.success('Habilitacion confirmada');
            fetchData();
        } catch (error) { toast.error('Error al confirmar'); }
    };

    const handleUploadEvidencia = async (habId, file) => {
        if (!file) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const token = localStorage.getItem('token');
            const res = await axios.post(`${API_URL}/api/habilitacion/${habId}/evidencia`, formData, {
                headers: { 'Content-Type': 'multipart/form-data', Authorization: `Bearer ${token}` }
            });
            toast.success('Evidencia subida');
            fetchData();
        } catch { toast.error('Error al subir evidencia'); }
        finally { setUploading(false); if (fileInputRef.current) fileInputRef.current.value = ''; }
    };

    const getCorteRelacion = (corteId) => {
        const corte = cortes.find(c => c.id === corteId);
        return corte ? corte.relacion_corte : '-';
    };

    const formatAvios = (avios) => {
        if (!avios || !Array.isArray(avios) || avios.length === 0) return '-';
        return avios.map(a => `${a.tipo}: ${a.cantidad}`).join(', ');
    };

    const pendientes = registros.filter(r => !r.confirmado);
    const confirmados = registros.filter(r => r.confirmado);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-400"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fadeIn" data-testid="habilitacion-page">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-zinc-100">Habilitacion</h1>
                    <p className="text-zinc-400 mt-1">Registro de pantalones terminados para habilitacion</p>
                </div>
                <Button
                    onClick={() => { resetFormState(); setModalOpen(true); }}
                    className="bg-amber-400 hover:bg-amber-500 text-zinc-900"
                    data-testid="add-habilitacion-btn"
                >
                    <Plus className="w-5 h-5 mr-2" />
                    Nueva Habilitacion
                </Button>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Card className="card">
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-yellow-400/10 flex items-center justify-center">
                            <Clock className="w-6 h-6 text-yellow-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-zinc-100">{pendientes.length}</p>
                            <p className="text-sm text-zinc-400">Pendientes de Confirmar</p>
                        </div>
                    </CardContent>
                </Card>
                <Card className="card">
                    <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-emerald-400/10 flex items-center justify-center">
                            <CheckCircle className="w-6 h-6 text-emerald-400" />
                        </div>
                        <div>
                            <p className="text-2xl font-bold text-zinc-100">{confirmados.length}</p>
                            <p className="text-sm text-zinc-400">Confirmados</p>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Pendientes */}
            <Card className="card">
                <div className="p-4 border-b border-zinc-700">
                    <h3 className="font-semibold text-zinc-100 flex items-center gap-2">
                        <Package className="w-5 h-5 text-amber-400" />
                        Habilitaciones Pendientes
                    </h3>
                </div>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-zinc-800">
                                    <TableHead className="table-header">Ticket</TableHead>
                                    <TableHead className="table-header">Fecha</TableHead>
                                    <TableHead className="table-header">Corte</TableHead>
                                    <TableHead className="table-header">Cantidad</TableHead>
                                    <TableHead className="table-header">Avios</TableHead>
                                    <TableHead className="table-header">Maquilador</TableHead>
                                    <TableHead className="table-header">Evidencia</TableHead>
                                    <TableHead className="table-header text-right">Acciones</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {pendientes.length > 0 ? pendientes.map((registro) => (
                                    <TableRow key={registro.id} className="table-row" data-testid={`hab-row-${registro.id}`}>
                                        <TableCell className="table-cell">
                                            <div className="flex items-center gap-2">
                                                <Ticket className="w-4 h-4 text-amber-400" />
                                                <span className="font-mono text-sm">{registro.ticket}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell className="table-cell">{registro.fecha}</TableCell>
                                        <TableCell className="table-cell font-medium">{getCorteRelacion(registro.corte_id)}</TableCell>
                                        <TableCell className="table-cell">
                                            <span className="badge-info">{registro.cantidad} pzas</span>
                                        </TableCell>
                                        <TableCell className="table-cell text-sm text-zinc-400">
                                            {formatAvios(registro.avios)}
                                        </TableCell>
                                        <TableCell className="table-cell">{registro.maquilador_nombre || registro.responsable || '-'}</TableCell>
                                        <TableCell className="table-cell">
                                            {registro.evidencia_url ? (
                                                <Button variant="ghost" size="sm" onClick={() => setEvidenciaModal(registro)} data-testid={`ver-evidencia-${registro.id}`}>
                                                    <Eye className="w-4 h-4 text-emerald-400" />
                                                </Button>
                                            ) : (
                                                <label className="cursor-pointer">
                                                    <input type="file" accept="image/*" className="hidden"
                                                        onChange={(e) => handleUploadEvidencia(registro.id, e.target.files?.[0])} />
                                                    <div className="flex items-center gap-1 text-xs text-zinc-500 hover:text-amber-400 transition-colors" data-testid={`upload-evidencia-${registro.id}`}>
                                                        <Upload className="w-3 h-3" /> Subir
                                                    </div>
                                                </label>
                                            )}
                                        </TableCell>
                                        <TableCell className="table-cell text-right">
                                            <div className="flex justify-end gap-1">
                                                <Button variant="ghost" size="sm" onClick={() => openEditModal(registro)} data-testid={`edit-hab-${registro.id}`}>
                                                    <Pencil className="w-4 h-4 text-amber-400" />
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    onClick={() => handleConfirmar(registro.id)}
                                                    className="bg-emerald-500 hover:bg-emerald-600"
                                                    data-testid={`confirmar-hab-${registro.id}`}
                                                >
                                                    <CheckCircle className="w-4 h-4 mr-1" />
                                                    Confirmar
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={8} className="text-center py-8 text-zinc-400">
                                            No hay habilitaciones pendientes
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            {/* Confirmados */}
            <Card className="card">
                <div className="p-4 border-b border-zinc-700">
                    <h3 className="font-semibold text-zinc-100 flex items-center gap-2">
                        <CheckCircle className="w-5 h-5 text-emerald-400" />
                        Historial Confirmado
                    </h3>
                </div>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-zinc-800">
                                    <TableHead className="table-header">Ticket</TableHead>
                                    <TableHead className="table-header">Fecha</TableHead>
                                    <TableHead className="table-header">Corte</TableHead>
                                    <TableHead className="table-header">Cantidad</TableHead>
                                    <TableHead className="table-header">Avios</TableHead>
                                    <TableHead className="table-header">Maquilador</TableHead>
                                    <TableHead className="table-header">Evidencia</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {confirmados.length > 0 ? confirmados.map((registro) => (
                                    <TableRow key={registro.id} className="table-row">
                                        <TableCell className="table-cell">
                                            <span className="font-mono text-sm">{registro.ticket}</span>
                                        </TableCell>
                                        <TableCell className="table-cell">{registro.fecha}</TableCell>
                                        <TableCell className="table-cell font-medium">{getCorteRelacion(registro.corte_id)}</TableCell>
                                        <TableCell className="table-cell">{registro.cantidad}</TableCell>
                                        <TableCell className="table-cell text-sm">{formatAvios(registro.avios)}</TableCell>
                                        <TableCell className="table-cell">{registro.maquilador_nombre || registro.responsable || '-'}</TableCell>
                                        <TableCell className="table-cell">
                                            {registro.evidencia_url ? (
                                                <Button variant="ghost" size="sm" onClick={() => setEvidenciaModal(registro)}>
                                                    <Eye className="w-4 h-4 text-emerald-400" />
                                                </Button>
                                            ) : <span className="text-xs text-zinc-500">-</span>}
                                        </TableCell>
                                    </TableRow>
                                )) : (
                                    <TableRow>
                                        <TableCell colSpan={7} className="text-center py-8 text-zinc-400">
                                            No hay registros confirmados
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>

            {/* Create/Edit Modal */}
            <Dialog open={modalOpen} onOpenChange={(open) => { setModalOpen(open); if (!open) setEditMode(null); }}>
                <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto" data-testid="habilitacion-modal">
                    <DialogHeader>
                        <DialogTitle>
                            <Package className="w-5 h-5 inline mr-2 text-amber-400" />
                            {editMode ? 'Editar Habilitacion' : 'Nueva Habilitacion'}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={editMode ? (e) => { e.preventDefault(); handleEditSubmit(); } : handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="form-group">
                                <Label className="form-label">Fecha *</Label>
                                <Input
                                    type="date"
                                    value={form.fecha}
                                    onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                                    data-testid="hab-fecha"
                                />
                            </div>
                            <div className="form-group">
                                <Label className="form-label">Cantidad *</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    value={form.cantidad}
                                    onChange={(e) => setForm({ ...form, cantidad: parseInt(e.target.value) || 0 })}
                                    data-testid="hab-cantidad"
                                />
                            </div>
                        </div>

                        <div className="form-group">
                            <Label className="form-label">Corte *</Label>
                            <Select value={form.corte_id} onValueChange={handleCorteChange}>
                                <SelectTrigger data-testid="hab-corte">
                                    <SelectValue placeholder="Seleccionar corte" />
                                </SelectTrigger>
                                <SelectContent>
                                    {cortes.map((c) => (
                                        <SelectItem key={c.id} value={c.id}>{c.relacion_corte} - {c.marca}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="form-group">
                            <Label className="form-label">Maquilador Responsable *</Label>
                            <Select value={form.maquilador_id} onValueChange={handleMaquiladorChange}>
                                <SelectTrigger data-testid="hab-maquilador">
                                    <SelectValue placeholder="Seleccionar maquilador" />
                                </SelectTrigger>
                                <SelectContent>
                                    {maquiladores.map((e) => (
                                        <SelectItem key={e.id} value={e.id}>{e.nombre}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            {form.maquilador_id && (() => {
                                const maq = empleados.find(e => e.id === form.maquilador_id);
                                const precioPieza = maq?.precio_pieza || maq?.precio_por_pieza;
                                return precioPieza ? (
                                    <p className="text-xs text-zinc-400 mt-1">
                                        Precio por pieza: <span className="text-amber-400 font-semibold">${precioPieza}</span>
                                        {form.cantidad > 0 && (
                                            <span className="ml-2">| Deuda estimada: <span className="text-red-400 font-semibold">${(precioPieza * form.cantidad).toFixed(2)}</span></span>
                                        )}
                                    </p>
                                ) : null;
                            })()}
                        </div>

                        <div className="p-4 bg-zinc-800 rounded-lg">
                            <Label className="form-label mb-3 block">Avios Entregados</Label>
                            <div className="grid grid-cols-2 gap-4">
                                <div className="form-group">
                                    <Label className="text-sm text-zinc-400">Carton</Label>
                                    <Input type="number" min="0" value={aviosCarton} onChange={(e) => setAviosCarton(parseInt(e.target.value) || 0)} data-testid="hab-carton" />
                                </div>
                                <div className="form-group">
                                    <Label className="text-sm text-zinc-400">Boton</Label>
                                    <Input type="number" min="0" value={aviosBoton} onChange={(e) => setAviosBoton(parseInt(e.target.value) || 0)} data-testid="hab-boton" />
                                </div>
                                <div className="form-group">
                                    <Label className="text-sm text-zinc-400">Remache</Label>
                                    <Input type="number" min="0" value={aviosRemache} onChange={(e) => setAviosRemache(parseInt(e.target.value) || 0)} data-testid="hab-remache" />
                                </div>
                            </div>
                            <div className="mt-3 pt-3 border-t border-zinc-700">
                                <Label className="text-sm text-zinc-400 mb-2 block">Ticket (Evidencia de entrega)</Label>
                                <input type="file" accept="image/*" ref={ticketInputRef} onChange={handleTicketFileChange} className="hidden" />
                                {ticketPreview ? (
                                    <div className="relative">
                                        <img src={ticketPreview} alt="Ticket" className="w-full h-32 object-cover rounded-lg border border-zinc-700" />
                                        <div className="absolute inset-0 bg-black/30 rounded-lg flex items-center justify-center opacity-0 hover:opacity-100 transition-opacity">
                                            <Button type="button" size="sm" onClick={() => ticketInputRef.current?.click()} className="bg-amber-400 hover:bg-amber-500 text-zinc-900">
                                                <Upload className="w-3 h-3 mr-1" /> Cambiar
                                            </Button>
                                        </div>
                                    </div>
                                ) : (
                                    <button type="button" onClick={() => ticketInputRef.current?.click()}
                                        className="w-full h-24 border-2 border-dashed border-zinc-700 rounded-lg flex flex-col items-center justify-center gap-1 text-zinc-500 hover:border-amber-400 hover:text-amber-400 transition-colors"
                                        data-testid="upload-ticket-btn">
                                        <Upload className="w-5 h-5" />
                                        <span className="text-xs">Subir foto del ticket</span>
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="form-group">
                            <Label className="form-label">Observaciones</Label>
                            <Textarea
                                value={form.observaciones}
                                onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
                                placeholder="Notas adicionales"
                                rows={2}
                                data-testid="hab-observaciones"
                            />
                        </div>

                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => { setModalOpen(false); setEditMode(null); }}>
                                Cancelar
                            </Button>
                            <Button type="submit" className="bg-amber-400 hover:bg-amber-500 text-zinc-900" data-testid="save-hab-btn">
                                {editMode ? 'Actualizar' : 'Registrar'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Evidencia Modal */}
            <Dialog open={!!evidenciaModal} onOpenChange={() => setEvidenciaModal(null)}>
                <DialogContent className="sm:max-w-[500px]" data-testid="evidencia-modal">
                    <DialogHeader>
                        <DialogTitle>
                            <Image className="w-5 h-5 inline mr-2 text-amber-400" />
                            Evidencia de Entrega - {evidenciaModal?.ticket}
                        </DialogTitle>
                    </DialogHeader>
                    {evidenciaModal?.evidencia_url ? (
                        <img
                            src={`${API_URL}${evidenciaModal.evidencia_url}`}
                            alt="Evidencia"
                            className="w-full rounded-lg border border-zinc-700"
                            data-testid="evidencia-image"
                        />
                    ) : (
                        <p className="text-zinc-400 text-center py-8">Sin evidencia</p>
                    )}
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setEvidenciaModal(null)}>Cerrar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
